import { decodeJwt } from 'jose'

import { AUTHENTICATION_REQUIRED_HEADER } from '../config/auth'
import { recoverySessionDataSchema, type RecoveredAuthSession } from '../config/auth/session-recovery'
import { navigateToPaymentAwareAuthRecovery } from './kr-payment-auth-navigation'

interface BrowserAuthSessionOptions {
  origin: string
  fetch: typeof fetch
  onExpired: () => void | Promise<void>
  runExclusive?: <T>(operation: () => Promise<T>) => Promise<T>
  notifyLogout?: () => void
  onNavigationRecovery?: () => void | Promise<void>
}

class SessionRecoveryError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code)
  }
}

function tokenClaims(token: string) {
  try {
    return decodeJwt(token)
  } catch {
    return null
  }
}

function tokenSubject(token: string): string | undefined {
  const claims = tokenClaims(token)
  const subject = claims?.sub ?? claims?.userId
  return typeof subject === 'string' || typeof subject === 'number' ? String(subject) : undefined
}

function isNearExpiry(token: string) {
  const expiresAt = tokenClaims(token)?.exp
  return typeof expiresAt === 'number' && expiresAt <= Date.now() / 1000 + 30
}

export function isNewerAccessToken(candidate: string, current: string) {
  const issuedAt = tokenClaims(candidate)?.iat
  return issuedAt !== undefined && issuedAt > (tokenClaims(current)?.iat ?? 0)
}

function isAuthenticationFailure(response: Response, isBackendRequest = false) {
  return (
    response.status === 401 && (isBackendRequest || response.headers.get(AUTHENTICATION_REQUIRED_HEADER) === 'true')
  )
}

function recoveryFailureResponse(status = 503, code = 'AUTH_SESSION_UNAVAILABLE') {
  return Response.json(
    { error: { code, message: 'Authentication could not be restored. Please try again.' } },
    { status, headers: { 'Cache-Control': 'no-store' } },
  )
}

/** A browser-only session; token hints never replace backend authentication. */
export class BrowserAuthSession {
  private current: RecoveredAuthSession | null = null
  private userId: string | undefined
  private latestAccessToken: string | undefined
  private knownAccessTokens = new Set<string>()
  private restoredAccessTokens = new Set<string>()
  private listeners = new Set<() => void>()
  private pending: Promise<RecoveredAuthSession> | null = null
  private pendingUserId: string | undefined
  private logoutInFlight: Promise<string | undefined> | null = null
  private generation = 0
  private stopped = false
  private navigatingRecovery = false
  private expiredRecoveryNeeded = false
  private navigatingExpiredSession = false

  constructor(private readonly options: BrowserAuthSessionOptions) {}

  getSnapshot = () => this.current

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  setIdentity(userId: string, accessToken: string) {
    if (this.stopped) {
      return
    }

    if (this.userId && this.userId !== userId) {
      this.generation += 1
      this.knownAccessTokens.clear()
      this.restoredAccessTokens.clear()
      this.latestAccessToken = undefined
      this.current = null
      this.notify()
    }

    this.userId = userId
    this.knownAccessTokens.add(accessToken)
    if (!this.latestAccessToken || isNewerAccessToken(accessToken, this.latestAccessToken)) {
      this.latestAccessToken = accessToken
      if (this.current && this.current.accessToken !== accessToken) {
        this.current = null
        this.notify()
      }
    }
  }

  async getFreshAccessToken(accessToken: string): Promise<string> {
    if (this.stopped) {
      await this.navigateExpiredSession()
      throw new SessionRecoveryError(401, 'AUTH_SESSION_EXPIRED')
    }
    const requestedUserId = tokenSubject(accessToken) ?? this.userId
    if (this.userId && requestedUserId && this.userId !== requestedUserId) {
      throw new SessionRecoveryError(409, 'AUTH_SESSION_CHANGED')
    }
    const candidate =
      this.knownAccessTokens.has(accessToken) &&
      this.latestAccessToken &&
      !isNewerAccessToken(accessToken, this.latestAccessToken)
        ? this.latestAccessToken
        : accessToken
    if (!isNearExpiry(candidate)) {
      return candidate
    }
    if (!requestedUserId) {
      throw new SessionRecoveryError(503, 'AUTH_SESSION_UNAVAILABLE')
    }
    if (this.options.onNavigationRecovery) {
      await this.navigateToRecovery()
    }

    this.userId ??= requestedUserId
    this.knownAccessTokens.add(accessToken)
    const generation = this.generation
    const session = await this.recover(requestedUserId, candidate, generation)
    if (this.stopped || generation !== this.generation) {
      throw new DOMException('Session changed', 'AbortError')
    }
    return session.accessToken
  }

  async prepareLogout(shouldNotify = true) {
    const alreadyStopped = this.stopped
    this.stopped = true
    this.expiredRecoveryNeeded = false
    this.generation += 1
    if (shouldNotify && !alreadyStopped) {
      this.options.notifyLogout?.()
    }
    // Drain Set-Cookie from an existing recovery before logout clears it.
    // Aborting only the caller could allow a late recovery to restore the cookies.
    await this.pending?.catch(() => undefined)
    this.current = null
    this.latestAccessToken = undefined
    this.knownAccessTokens.clear()
    this.restoredAccessTokens.clear()
    this.notify()
  }

  logout(url: string): Promise<string | undefined> {
    this.logoutInFlight ??= this.performLogout(url)
    return this.logoutInFlight
  }

  private async performLogout(url: string): Promise<string | undefined> {
    // Draining inside the lock could deadlock against a queued recovery.
    await this.prepareLogout()
    return this.runExclusive(async () => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10_000)
      try {
        const response = await this.options.fetch(
          new Request(url, {
            credentials: 'same-origin',
            cache: 'no-store',
            signal: controller.signal,
          }),
        )
        if (!response.ok) {
          throw new SessionRecoveryError(503, 'AUTH_SESSION_UNAVAILABLE')
        }
        // Consume the final redirect response before releasing the cross-tab lock.
        await response.arrayBuffer()
        return response.url && new URL(response.url).origin === this.options.origin ? response.url : undefined
      } finally {
        clearTimeout(timeout)
      }
    })
  }

  async fetch(request: Request, options: { backendOrigin?: string } = {}): Promise<Response> {
    const url = new URL(request.url)
    const bearer = request.headers.get('Authorization')?.match(/^Bearer\s+(.+)$/i)?.[1]
    const isAuthFlow = /^\/api\/v\d+\/(authentication|oauth|auth)(\/|$)/.test(url.pathname)
    const isBackendRequest = options.backendOrigin === url.origin
    if (!bearer || isAuthFlow || (!isBackendRequest && url.origin !== this.options.origin)) {
      return this.options.fetch(request)
    }
    if (this.stopped) {
      await this.navigateExpiredSession()
      return recoveryFailureResponse(401, 'AUTH_SESSION_EXPIRED')
    }
    if (this.navigatingRecovery) {
      return recoveryFailureResponse()
    }

    const requestedUserId = tokenSubject(bearer)
    if (this.userId && requestedUserId && this.userId !== requestedUserId) {
      return recoveryFailureResponse(409, 'AUTH_SESSION_CHANGED')
    }
    this.userId ??= requestedUserId

    const generation = this.generation
    if (this.knownAccessTokens.has(bearer) && this.latestAccessToken) {
      request.headers.set('Authorization', `Bearer ${this.latestAccessToken}`)
    }
    const accessToken = request.headers.get('Authorization')!.slice(7)
    const retry = request.clone()
    const response = await this.options.fetch(request)
    if (this.stopped || generation !== this.generation) {
      throw new DOMException('Session changed', 'AbortError')
    }
    if (!isAuthenticationFailure(response, isBackendRequest) || request.signal.aborted) {
      return response
    }
    if (this.options.onNavigationRecovery) {
      try {
        await this.navigateToRecovery()
      } catch {
        return recoveryFailureResponse()
      }
    }

    // A fresh token rejected by the protected API must not drive a refresh/render loop.
    if (this.restoredAccessTokens.has(accessToken) && !isNearExpiry(accessToken)) {
      return recoveryFailureResponse()
    }

    const expectedUserId = tokenSubject(accessToken) ?? this.userId
    if (!expectedUserId) {
      return recoveryFailureResponse()
    }
    if (this.userId && expectedUserId !== this.userId) {
      return recoveryFailureResponse(409, 'AUTH_SESSION_CHANGED')
    }
    this.userId = expectedUserId
    this.knownAccessTokens.add(accessToken)

    let session: RecoveredAuthSession
    try {
      session = await this.recover(expectedUserId, accessToken, generation)
    } catch (error) {
      if (
        error instanceof SessionRecoveryError &&
        error.code === 'AUTH_SESSION_EXPIRED' &&
        generation === this.generation
      ) {
        return recoveryFailureResponse(401, error.code)
      }
      if (request.signal.aborted || this.stopped || generation !== this.generation) {
        throw new DOMException('Session changed', 'AbortError')
      }
      return error instanceof SessionRecoveryError
        ? recoveryFailureResponse(error.status, error.code)
        : recoveryFailureResponse()
    }
    if (request.signal.aborted || this.stopped || generation !== this.generation) {
      throw new DOMException('Session changed', 'AbortError')
    }

    // An authentication error can occur after a write reached a downstream service.
    // Renew credentials, but require an explicit retry of that action.
    if (!['GET', 'HEAD'].includes(retry.method)) {
      return recoveryFailureResponse(409, 'AUTH_SESSION_RETRY_REQUIRED')
    }

    retry.headers.set('Authorization', `Bearer ${session.accessToken}`)
    const retriedResponse = await this.options.fetch(retry)
    if (this.stopped || generation !== this.generation) {
      throw new DOMException('Session changed', 'AbortError')
    }
    return isAuthenticationFailure(retriedResponse, isBackendRequest) ? recoveryFailureResponse() : retriedResponse
  }

  private recover(expectedUserId: string, failedAccessToken: string, generation: number) {
    if (
      this.current &&
      this.current.accessToken !== failedAccessToken &&
      this.current.userId === expectedUserId &&
      !isNearExpiry(this.current.accessToken) &&
      !isNewerAccessToken(failedAccessToken, this.current.accessToken)
    ) {
      return Promise.resolve(this.current)
    }
    if (this.pending) {
      return this.pendingUserId === expectedUserId
        ? this.pending
        : Promise.reject(new SessionRecoveryError(409, 'AUTH_SESSION_CHANGED'))
    }

    const pending = this.runExclusive(async () => {
      if (this.stopped || generation !== this.generation) {
        throw new DOMException('Session changed', 'AbortError')
      }
      return this.requestRecovery(expectedUserId, generation)
    })
    this.pending = pending
    this.pendingUserId = expectedUserId
    void pending
      .finally(() => {
        if (this.pending === pending) {
          this.pending = null
          this.pendingUserId = undefined
        }
      })
      .catch(() => undefined)
    return pending
  }

  private async requestRecovery(expectedUserId: string, generation: number) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10_000)
    try {
      const response = await this.options.fetch(
        new Request(new URL('/api/v2/authentication/recover-session', this.options.origin), {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expectedUserId }),
          signal: controller.signal,
        }),
      )
      if (this.stopped || generation !== this.generation) {
        throw new DOMException('Session changed', 'AbortError')
      }
      const body: unknown = await response.json()
      if (this.stopped || generation !== this.generation) {
        throw new DOMException('Session changed', 'AbortError')
      }
      if (!response.ok) {
        const errorCode =
          body &&
          typeof body === 'object' &&
          'error' in body &&
          body.error &&
          typeof body.error === 'object' &&
          'code' in body.error
            ? body.error.code
            : undefined
        if (response.status === 401 && errorCode === 'AUTH_SESSION_EXPIRED') {
          this.stopped = true
          this.expiredRecoveryNeeded = true
          this.current = null
          this.notify()
          await this.navigateExpiredSession()
          throw new SessionRecoveryError(401, 'AUTH_SESSION_EXPIRED')
        }
        throw new SessionRecoveryError(
          response.status === 409 ? 409 : 503,
          response.status === 409 ? 'AUTH_SESSION_CHANGED' : 'AUTH_SESSION_UNAVAILABLE',
        )
      }
      const session = recoverySessionDataSchema.parse(
        body && typeof body === 'object' && 'data' in body ? body.data : undefined,
      )
      if (session.userId !== expectedUserId) {
        throw new SessionRecoveryError(409, 'AUTH_SESSION_CHANGED')
      }
      if (this.latestAccessToken && isNewerAccessToken(this.latestAccessToken, session.accessToken)) {
        // A newer server navigation may finish while this recovery is pending.
        // Its identity is already rendered; never replace it with the older response.
        return { ...session, accessToken: this.latestAccessToken }
      }
      this.knownAccessTokens.add(session.accessToken)
      this.restoredAccessTokens.add(session.accessToken)
      this.latestAccessToken = session.accessToken
      this.current = session
      this.notify()
      return session
    } finally {
      clearTimeout(timeout)
    }
  }

  private notify() {
    this.listeners.forEach((listener) => listener())
  }

  private async navigateToRecovery(): Promise<never> {
    if (!this.navigatingRecovery) {
      this.navigatingRecovery = true
      try {
        await this.options.onNavigationRecovery?.()
      } catch {
        // Sealing a return can fail before navigation starts. Keep the original URL retryable.
        this.navigatingRecovery = false
      }
    }
    throw new SessionRecoveryError(503, 'AUTH_SESSION_UNAVAILABLE')
  }

  private async navigateExpiredSession() {
    if (!this.expiredRecoveryNeeded || this.navigatingExpiredSession) {
      return
    }
    this.navigatingExpiredSession = true
    try {
      await this.options.onExpired()
    } catch {
      // Retry only navigation on the next explicit request; expired credentials remain unusable.
      this.navigatingExpiredSession = false
    }
  }

  private runExclusive<T>(operation: () => Promise<T>) {
    return this.options.runExclusive ? this.options.runExclusive(operation) : operation()
  }
}

let browserSession: BrowserAuthSession | undefined

export function getBrowserAuthSession(): BrowserAuthSession | null {
  if (typeof window === 'undefined') {
    return null
  }
  if (!browserSession) {
    const channel =
      typeof window.BroadcastChannel === 'function' ? new window.BroadcastChannel('podo-auth-session') : null
    const locks = window.navigator?.locks
    const logoutEventKey = 'podo_auth_logout_event'
    const session = new BrowserAuthSession({
      origin: window.location.origin,
      fetch: (input, init) => fetch(input, init),
      onExpired: () => navigateToPaymentAwareAuthRecovery(true),
      runExclusive: locks
        ? async (operation) => {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 15_000)
            try {
              return await locks.request('podo-auth-session', { signal: controller.signal }, operation)
            } finally {
              clearTimeout(timeout)
            }
          }
        : undefined,
      // Older browsers use the bounded server redirect instead of an uncoordinated
      // background Set-Cookie response that could race logout in another tab.
      onNavigationRecovery: locks ? undefined : () => navigateToPaymentAwareAuthRecovery(),
      notifyLogout: () => {
        channel?.postMessage('logout')
        try {
          // This is an event nonce only; no identity or credentials cross storage or channels.
          window.localStorage?.setItem(logoutEventKey, crypto.randomUUID())
        } catch {
          // Storage-disabled browsers still have BroadcastChannel or same-tab fencing.
        }
      },
    })
    channel?.addEventListener('message', (event) => {
      if (event.data === 'logout') {
        void session.prepareLogout(false)
      }
    })
    window.addEventListener?.('storage', (event) => {
      if (event.key === logoutEventKey && event.newValue) {
        void session.prepareLogout(false)
      }
    })
    browserSession = session
  }
  return browserSession
}
